import React from 'react'

import { V1RequestStatus } from '@/lib/api/lib/models'
import type { V1ProgrammeRequest } from '@/lib/api/lib/models'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'
import { Typography } from '@/components/ui/typography'

import { ChevronRight, Users, Clock } from 'lucide-react'

function StatBlock({
    value,
    label,
    icon,
    accentClass,
    pulse
}: {
    value: number
    label: string
    icon: React.ReactNode
    accentClass: string
    pulse?: boolean
}) {
    return (
        <div className="flex-1 relative overflow-hidden rounded-xl bg-background/60 dark:bg-background/30 p-3.5 backdrop-blur-sm">
            <div className="flex items-start justify-between mb-2">
                <div
                    className={`h-8 w-8 rounded-lg ${accentClass} flex items-center justify-center`}
                >
                    {icon}
                </div>
                {pulse && value > 0 && (
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="coach-pulse-ring absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                    </span>
                )}
            </div>
            <div className="coach-stat-value text-2xl font-bold tracking-tight text-foreground leading-none mb-0.5">
                {value}
            </div>
            <Typography variant="body-xs" className="text-muted-foreground">
                {label}
            </Typography>
        </div>
    )
}

const ACTIVE_COACH_STATUSES: string[] = [
    V1RequestStatus.Accepted,
    V1RequestStatus.InProgress,
    V1RequestStatus.Delivered
]

function CoachDashboard({ requests }: { requests: V1ProgrammeRequest[] }) {
    const appContext = React.useContext(AppContext)

    const pending = requests.filter(
        (r) => r.status === V1RequestStatus.Requested
    ).length

    const activeRequests = requests.filter((r) =>
        ACTIVE_COACH_STATUSES.includes(r.status as string)
    )
    const activeUsers = new Set(
        activeRequests.map((r) => r.requesterId).filter(Boolean)
    ).size

    return (
        <LinkWithState to="/requests/list?tab=coach" className="block">
            <div className="req-coach-card req-animate-in rounded-2xl p-3.5 border border-border/50 relative overflow-hidden coach-card-hover">
                <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-amber-500/[0.06] dark:bg-amber-400/[0.04] blur-xl pointer-events-none" />

                <div className="flex gap-2.5 mb-3">
                    <StatBlock
                        value={pending}
                        label={appContext.geti18n('requests.status.requested')}
                        icon={
                            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        }
                        accentClass="bg-amber-500/12 dark:bg-amber-500/15"
                        pulse
                    />
                    <StatBlock
                        value={activeUsers}
                        label={appContext.geti18n('requests.coach.activeUsers')}
                        icon={
                            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        }
                        accentClass="bg-blue-500/12 dark:bg-blue-500/15"
                    />
                </div>

                <div className="flex items-center justify-between px-1">
                    <Typography
                        variant="body-xs"
                        className="font-medium text-muted-foreground"
                    >
                        {appContext.geti18n('requests.coach.myRequests')}
                    </Typography>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
            </div>
        </LinkWithState>
    )
}

export default CoachDashboard
